// DOM helpers and dialogs for the web app, drawn with the same shadcn recipes
// in web/styles.css (.cthd-btn, .cthd-badge, .cthd-modal ...). Icons are Lucide.
import {
  createElement, Folder, FolderOpen, FolderPlus, FolderInput, FileImage, Plus, Upload, Download, Search, SearchX,
  ChevronRight, ChevronDown, Ellipsis, Link, Image, Layers, Clapperboard, FlipHorizontal2, Undo2, Redo2, Keyboard,
  Pencil, CopyPlus, Trash2, X, ArrowUp, ArrowDown, ArrowDownUp, TriangleAlert, Settings, LogOut, PanelLeft,
  PanelRight, RefreshCw, Check, Cloud, Copy, ExternalLink, Menu as MenuIcon, WifiOff, LogIn, RotateCw, PencilRuler,
} from 'lucide';

const ICONS = {
  folder: Folder, 'folder-open': FolderOpen, 'folder-plus': FolderPlus, 'folder-input': FolderInput, 'file-image': FileImage,
  plus: Plus, upload: Upload, download: Download, search: Search, 'search-x': SearchX, 'chevron-right': ChevronRight,
  'chevron-down': ChevronDown, 'more-horizontal': Ellipsis, link: Link, image: Image, layers: Layers, clapperboard: Clapperboard,
  'flip-horizontal-2': FlipHorizontal2, 'undo-2': Undo2, 'redo-2': Redo2, keyboard: Keyboard, pencil: Pencil, 'copy-plus': CopyPlus,
  'trash-2': Trash2, x: X, 'arrow-up': ArrowUp, 'arrow-down': ArrowDown, 'arrow-down-up': ArrowDownUp, 'triangle-alert': TriangleAlert,
  settings: Settings, 'log-out': LogOut, 'panel-left': PanelLeft, 'panel-right': PanelRight, 'refresh-cw': RefreshCw, check: Check,
  cloud: Cloud, copy: Copy, 'external-link': ExternalLink, menu: MenuIcon, 'wifi-off': WifiOff, 'log-in': LogIn, 'rotate-cw': RotateCw, 'cth-rink': PencilRuler, // the app's icon: a generic drawing icon (was a rink outline)
};

export function h(tag, attrs = {}, ...kids) {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs || {})) {
    if (v == null || v === false) continue;
    if (k === 'class') el.className = v;
    else if (k === 'text') el.textContent = v;
    else if (k === 'style' && typeof v === 'object') Object.assign(el.style, v);
    else if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2), v);
    else if (v === true) el.setAttribute(k, '');
    else el.setAttribute(k, String(v));
  }
  for (const c of kids.flat()) if (c != null && c !== false) el.append(c instanceof Node ? c : document.createTextNode(String(c)));
  return el;
}

export function icon(name, cls = '') {
  const s = h('span', { class: `cthd-ic ${cls}`.trim(), 'aria-hidden': 'true' });
  const node = ICONS[name];
  if (node) s.append(createElement(node));
  return s;
}

/** shadcn Button. Icon-only buttons get their label as the tooltip. */
export function button({ label, iconName, variant = 'outline', size = 'default', onClick, tip, danger, disabled, cls = '' }) {
  const iconOnly = size === 'icon' || size === 'icon-sm';
  const t = iconOnly ? (tip || label) : tip;
  const b = h('button', {
    class: `cthd-btn is-${variant}${size === 'default' ? '' : ` is-${size}`}${danger ? ' is-danger' : ''} ${cls}`.trim(),
    type: 'button', 'aria-label': t || null, title: t || null, disabled: disabled || null,
  });
  if (iconName) b.append(icon(iconName));
  if (!iconOnly && label) b.append(h('span', { text: label }));
  if (onClick) b.addEventListener('click', (e) => onClick(e, b));
  return b;
}

export function badge(text, variant = 'secondary', iconName = null) {
  const b = h('span', { class: `cthd-badge is-${variant}` });
  if (iconName) b.append(icon(iconName));
  b.append(h('span', { text }));
  return b;
}

// ---- toasts ------------------------------------------------------------------

let toastBox = null;
const recent = new Map(); // message -> when it was last shown
// Raw errors in plain English, and the same message twice within 4 s shows once.
const friendly = (t) => String(t)
  .replace(/\((TypeError: )?(Failed to fetch|Load failed|NetworkError[^)]*)\)/g, '(no connection)')
  .replace(/(TypeError: )?(Failed to fetch|Load failed)/g, 'no connection')
  .replace(/NOT_CONNECTED/g, 'Dropbox signed you out');
export function toast(msg, ms = 2600) {
  if (!(msg instanceof Node)) {
    msg = friendly(msg);
    const last = recent.get(msg);
    if (last && Date.now() - last < 4000) return;
    recent.set(msg, Date.now());
  }
  if (!toastBox) { toastBox = h('div', { class: 'cthw-toasts cthd-layer', role: 'status', 'aria-live': 'polite' }); document.body.append(toastBox); }
  const t = h('div', { class: 'cthw-toast' });
  if (msg instanceof Node) t.append(msg); else t.textContent = String(msg);
  const close = () => { t.classList.add('is-out'); setTimeout(() => t.remove(), 200); };
  t.addEventListener('click', (e) => { if (!(e.target instanceof HTMLAnchorElement) && !(e.target instanceof HTMLButtonElement)) close(); });
  toastBox.append(t);
  setTimeout(close, ms);
}

// ---- dialogs -----------------------------------------------------------------

export function isModalOpen() { return !!document.querySelector('.cthw-overlay'); }

/** A shadcn Dialog. Escape and a click outside close it; focus stays inside. */
export function modal(title, desc = '', { wide = false, focusFirst = true } = {}) {
  const content = h('div', { class: 'cthw-modal-content' });
  const box = h('div', { class: `cthd-modal cthw-modal${wide ? ' is-wide' : ''}`, role: 'dialog', 'aria-modal': 'true', 'aria-label': title },
    h('div', { class: 'cthw-modal-head' }, h('h2', { class: 'cthw-modal-title', text: title }),
      button({ iconName: 'x', size: 'icon-sm', variant: 'ghost', tip: 'Close', cls: 'cthw-modal-x', onClick: () => api.close() })),
    desc ? h('p', { class: 'cthd-modal-desc', text: desc }) : null,
    content);
  const overlay = h('div', { class: 'cthw-overlay' }, box);
  const before = document.activeElement;
  const onKey = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); api.close(); }
    if (e.key === 'Tab') {
      const f = [...box.querySelectorAll('button, input, select, textarea, a[href], [tabindex]:not([tabindex="-1"])')].filter((x) => !x.disabled && x.offsetParent);
      if (!f.length) return;
      if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
      else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
    }
  };
  overlay.addEventListener('pointerdown', (e) => { if (e.target === overlay) api.close(); });
  const api = {
    el: box, content, onClose: null, closed: false,
    footer(...buttons) { const f = h('div', { class: 'cthd-modal-footer' }, ...buttons); content.append(f); return f; },
    open() { document.body.append(overlay); window.addEventListener('keydown', onKey, true); setTimeout(() => ((focusFirst && box.querySelector('input, [autofocus]')) || box.querySelector('.cthd-modal-footer .is-default') || box).focus?.(), 0); return api; },
    close() {
      if (api.closed) return;
      api.closed = true;
      window.removeEventListener('keydown', onKey, true);
      overlay.remove();
      before?.focus?.({ preventScroll: true });
      api.onClose?.();
    },
  };
  box.tabIndex = -1;
  return api;
}

export function confirm({ title, body = '', action = 'Delete', danger = true }) {
  return new Promise((res) => {
    const m = modal(title, body);
    const go = button({ label: action, variant: danger ? 'destructive' : 'default', onClick: () => { res(true); m.onClose = null; m.close(); } });
    m.footer(button({ label: 'Cancel', variant: 'outline', onClick: () => m.close() }), go);
    m.onClose = () => res(false);
    m.open();
    setTimeout(() => go.focus(), 0);
  });
}

export function prompt({ title, label = '', value = '', placeholder = '', action = 'Save', desc = '' }) {
  return new Promise((res) => {
    const m = modal(title, desc);
    const input = h('input', { class: 'cthd-input', type: 'text', value, placeholder, spellcheck: 'false', 'aria-label': label || title });
    const finish = (v) => { m.onClose = null; res(v); m.close(); };
    if (label) m.content.append(h('label', { class: 'cthd-label', text: label }));
    m.content.append(input);
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter' && !e.isComposing) { e.preventDefault(); finish(input.value); } });
    m.footer(button({ label: 'Cancel', variant: 'outline', onClick: () => m.close() }), button({ label: action, variant: 'default', onClick: () => finish(input.value) }));
    m.onClose = () => res(null);
    m.open();
    setTimeout(() => { input.focus(); input.select(); }, 0);
  });
}

/** Pick one of `items` ({ label, value }) with a filter box. */
export function pickFrom(title, items, placeholder = 'Type to filter') {
  return new Promise((res) => {
    const m = modal(title);
    const input = h('input', { class: 'cthd-input', type: 'search', placeholder, spellcheck: 'false', 'aria-label': placeholder });
    const list = h('div', { class: 'cthw-picklist', role: 'listbox' });
    let active = 0;
    let shown = items;
    const finish = (v) => { m.onClose = null; res(v); m.close(); };
    const paint = () => {
      const q = input.value.trim().toLowerCase();
      shown = items.filter((i) => i.label.toLowerCase().includes(q));
      active = Math.min(active, Math.max(0, shown.length - 1));
      list.replaceChildren(...shown.map((it, k) => {
        const row = h('button', { class: `cthw-pickrow${k === active ? ' is-active' : ''}`, type: 'button', role: 'option' }, icon(it.icon || 'folder'), h('span', { text: it.label }));
        row.addEventListener('click', () => finish(it.value));
        return row;
      }));
      if (!shown.length) list.append(h('div', { class: 'cthd-hint', text: 'Nothing matches.' }));
    };
    input.addEventListener('input', () => { active = 0; paint(); });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); active = Math.min(shown.length - 1, active + 1); paint(); }
      if (e.key === 'ArrowUp') { e.preventDefault(); active = Math.max(0, active - 1); paint(); }
      if (e.key === 'Enter' && shown[active]) { e.preventDefault(); finish(shown[active].value); }
    });
    m.content.append(input, list);
    m.onClose = () => res(null);
    paint();
    m.open();
  });
}

// ---- menus -------------------------------------------------------------------

/** A shadcn DropdownMenu at the pointer. items: { label, icon, run, danger } or 'sep'. */
export function menu(e, items) {
  document.querySelector('.cthw-menu')?.remove();
  const m = h('div', { class: 'cthw-menu cthd-layer', role: 'menu' });
  for (const it of items) {
    if (it === 'sep') { m.append(h('div', { class: 'cthw-menu-sep' })); continue; }
    if (!it) continue;
    const row = h('button', { class: `cthw-menu-item${it.danger ? ' is-danger' : ''}`, type: 'button', role: 'menuitem' }, icon(it.checked ? 'check' : it.icon || ''), h('span', { text: it.label }));
    row.addEventListener('click', () => { close(); it.run(); });
    m.append(row);
  }
  document.body.append(m);
  const r = m.getBoundingClientRect();
  const x = Math.min(e.clientX, innerWidth - r.width - 8);
  const y = Math.min(e.clientY, innerHeight - r.height - 8);
  m.style.left = `${Math.max(8, x)}px`;
  m.style.top = `${Math.max(8, y)}px`;
  const away = (ev) => { if (!m.contains(ev.target)) close(); };
  const key = (ev) => { if (ev.key === 'Escape') { ev.stopPropagation(); close(); } };
  function close() { m.remove(); window.removeEventListener('pointerdown', away, true); window.removeEventListener('keydown', key, true); }
  setTimeout(() => { window.addEventListener('pointerdown', away, true); window.addEventListener('keydown', key, true); }, 0);
  m.querySelector('button')?.focus();
}

// ---- shortcuts and rinks -------------------------------------------------------

export const SHORTCUTS = [
  ['V', 'Select and move'], ['A / S / Z / X / P', 'Skate / Skate with puck / Skate backwards / Shoot / Pass'], ['B / C', 'Shaded box / Circle'],
  ['T / E', 'Text / Pen'], ['1 / 2 / 3', 'Players (hover a player button for preset letters)'],
  ['F', '5v5 faceoff'], ['H / N / K / U / O / W', 'Coach / Net / Puck / Pucks / Cone / Border'],
  ['6 / 7 / 8 / 9', 'Colour presets (recolours the selection too)'], ['+ / -', 'Add / Remove a rink'],
  ['Cmd+Z / Shift+Cmd+Z', 'Undo / Redo'], ['Cmd+C / X / V', 'Copy / Cut / Paste selection (works across diagrams)'],
  ['Cmd+D', 'Duplicate selection'], ['Delete', 'Remove selection'], ['[ / ]', 'Send back / Bring forward'],
  ['Arrows', 'Nudge (Shift = larger step)'], ['Cmd while dragging', 'Snapping off'],
  ['Escape / Return', 'Step out: popup, then tool, then selection, then rink'], ['Shift-click', 'Add to selection'],
  ['Round handle', 'Rotate the selected item (snaps to 15 degrees, Cmd = free)'],
  ['Double-click', 'Edit text, letter a player, label a shape'],
  ['Right-click a tool', 'Its menu: options, your own single-key shortcut, move it along the row'],
  ['Cmd+S', 'Save now (it also saves itself a second after you stop)'],
  ['?', 'This list'],
];

export function shortcutsModal() {
  const m = modal('Keyboard shortcuts', 'Keys work while a diagram is open.', { wide: true });
  const grid = h('div', { class: 'cthd-keys' });
  for (const [k, v] of SHORTCUTS) grid.append(h('span', { class: 'cthd-kbd-wrap' }, ...k.split(' / ').flatMap((part, i) => [i ? h('span', { class: 'cthd-kbd-sep', text: '/' }) : null, h('kbd', { class: 'cthd-kbd', text: part })])), h('span', { class: 'cthd-keys-v', text: v }));
  m.content.append(grid);
  m.footer(button({ label: 'Done', variant: 'default', onClick: () => m.close() }));
  m.open();
}

export function rinksModal({ seq, names }) {
  return new Promise((res) => {
    const m = modal('Export rinks', 'Choose which rinks of this sequence to copy or download as a picture.');
    const boxes = [];
    const list = h('div', { class: 'cthd-rinklist' });
    for (let k = 0; k < seq; k++) {
      const cb = h('input', { type: 'checkbox', class: 'cthd-check', checked: true });
      boxes.push(cb);
      list.append(h('label', { class: 'cthd-rinkrow' }, cb, h('span', { text: (names[k] || '').trim() || `Rink ${k + 1}` })));
    }
    m.content.append(list);
    const finish = (action) => {
      const frames = boxes.map((b, k) => (b.checked ? k : -1)).filter((k) => k >= 0);
      if (!frames.length) return;
      m.onClose = null; res({ frames, action }); m.close();
    };
    m.footer(
      button({ label: 'Cancel', variant: 'outline', onClick: () => m.close() }),
      button({ label: 'Copy picture', iconName: 'copy', variant: 'outline', onClick: () => finish('copy') }),
      button({ label: 'Download PNG', iconName: 'download', variant: 'default', onClick: () => finish('save') }));
    m.onClose = () => res(null);
    m.open();
  });
}

export function guideModal(markdown) {
  const m = modal('How to get a good animation', '', { wide: true });
  const body = h('div', { class: 'cthd-guide cthw-guide' });
  // The guide is plain Markdown: headings, paragraphs and bullet lists.
  let ul = null;
  for (const line of String(markdown).split('\n')) {
    const t = line.trim();
    if (!t) { ul = null; continue; }
    const hm = t.match(/^(#{1,4})\s+(.*)$/);
    if (hm) { ul = null; body.append(h(`h${Math.min(4, hm[1].length + 1)}`, { text: hm[2] })); continue; }
    const lm = t.match(/^[-*]\s+(.*)$/);
    if (lm) { if (!ul) { ul = h('ul'); body.append(ul); } ul.append(h('li', { text: lm[1].replace(/\*\*/g, '') })); continue; }
    ul = null;
    body.append(h('p', { text: t.replace(/\*\*/g, '') }));
  }
  m.content.append(body);
  m.footer(button({ label: 'Done', variant: 'default', onClick: () => m.close() }));
  m.open();
}
